import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { filterPlaceholderAddressLines, formatBusinessUenLine, splitMultilineAddressLines } from "../../utils/invoice-address";

export interface CoreInvoicePreviewLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface CoreInvoicePartyDisplayOptions {
  enabled?: boolean;
  name?: boolean;
  email?: boolean;
  phone?: boolean;
  address?: boolean;
  uen?: boolean;
}

export interface CoreInvoiceDisplayOptions {
  logo?: boolean;
  business?: CoreInvoicePartyDisplayOptions;
  invoiceNumber?: boolean;
  date?: boolean;
  client?: CoreInvoicePartyDisplayOptions;
  paymentTerms?: boolean;
  termsAndConditions?: boolean;
}

@Component({
  selector: "core-invoice-preview",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./core-invoice-preview.component.html",
  styleUrl: "./core-invoice-preview.component.scss",
})
export class CoreInvoicePreviewComponent {
  @Input() logoUrl = "";
  @Input() initials = "HL";
  @Input() businessName = "";
  @Input() businessEmail = "";
  @Input() businessPhone = "";
  /** Business UEN — shown as `UEN {value}` when display option is on. */
  @Input() businessUen = "";
  /** Legacy single-string fallback; prefer `businessAddressLines` (matches PDF line layout). */
  @Input() businessAddress = "";
  /** Street line + city/state/postal line — one preview row per entry, same as PDF. */
  @Input() businessAddressLines: string[] = [];
  @Input() businessMeta = "";
  @Input() invoiceNumber = "Draft";
  @Input() invoiceDate = "";
  @Input() billToName = "";
  /** e.g. "Attn to Sarah Chen" under company Bill-to — same as PDF. */
  @Input() billToAttn = "";
  @Input() billToMeta = "";
  @Input() billToPhone = "";
  /** Legacy single-string fallback; prefer `billToAddressLines` (matches PDF line layout). */
  @Input() billToAddress = "";
  /** Client billing address — one preview row per line, same as PDF. */
  @Input() billToAddressLines: string[] = [];
  @Input() lineItems: CoreInvoicePreviewLineItem[] = [];
  @Input() subtotal = 0;
  @Input() gstAmount = 0;
  @Input() includeGst = false;
  @Input() gstInclusive = false;
  @Input() total = 0;
  @Input() currency = "SGD";
  @Input() paymentSummary = "";
  @Input() termsText = "";
  @Input() tableClass = "";
  @Input() density: "default" | "business-light" = "default";
  @Input() displayOptions: CoreInvoiceDisplayOptions | null = null;

  get normalizedDisplayOptions(): Required<CoreInvoiceDisplayOptions> & {
    business: Required<CoreInvoicePartyDisplayOptions>;
    client: Required<CoreInvoicePartyDisplayOptions>;
  } {
    const options = this.displayOptions ?? {};
    return {
      logo: options.logo ?? true,
      business: {
        enabled: options.business?.enabled ?? true,
        name: options.business?.name ?? true,
        email: options.business?.email ?? true,
        phone: options.business?.phone ?? true,
        address: options.business?.address ?? true,
        uen: options.business?.uen ?? true,
      },
      invoiceNumber: options.invoiceNumber ?? true,
      date: options.date ?? true,
      client: {
        enabled: options.client?.enabled ?? true,
        name: options.client?.name ?? true,
        email: options.client?.email ?? true,
        phone: options.client?.phone ?? true,
        address: options.client?.address ?? true,
        uen: options.client?.uen ?? true,
      },
      paymentTerms: options.paymentTerms ?? true,
      termsAndConditions: options.termsAndConditions ?? true,
    };
  }

  get showLogo(): boolean {
    return this.normalizedDisplayOptions.logo && !!this.logoUrl;
  }

  get showBusiness(): boolean {
    const options = this.normalizedDisplayOptions.business;
    return options.enabled && (options.name || this.businessMetaLines.length > 0);
  }

  get showBusinessName(): boolean {
    const options = this.normalizedDisplayOptions.business;
    return options.enabled && options.name;
  }

  get businessMetaLines(): string[] {
    const options = this.normalizedDisplayOptions.business;
    if (!options.enabled) return [];

    const addressLines = this.resolvedBusinessAddressLines;
    const uenLine = formatBusinessUenLine(this.businessUen);
    const hasStructuredContact =
      !!String(this.businessEmail ?? "").trim() ||
      !!String(this.businessPhone ?? "").trim() ||
      !!uenLine ||
      addressLines.length > 0 ||
      !!String(this.businessAddress ?? "").trim();

    if (hasStructuredContact) {
      return [
        ...(options.email ? [this.businessEmail] : []),
        ...(options.phone ? [this.businessPhone] : []),
        ...(options.uen && uenLine ? [uenLine] : []),
        ...(options.address ? addressLines : []),
      ]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean);
    }

    return filterPlaceholderAddressLines(this.splitLines(this.businessMeta));
  }

  private get resolvedBusinessAddressLines(): string[] {
    const fromLines = filterPlaceholderAddressLines(this.businessAddressLines);
    if (fromLines.length) return fromLines;
    return splitMultilineAddressLines(this.businessAddress);
  }

  get showInvoiceNumber(): boolean {
    return this.normalizedDisplayOptions.invoiceNumber;
  }

  get showInvoiceDate(): boolean {
    return this.normalizedDisplayOptions.date;
  }

  get showClient(): boolean {
    const options = this.normalizedDisplayOptions.client;
    return options.enabled && (options.name || this.clientMetaLines.length > 0);
  }

  get showClientName(): boolean {
    const options = this.normalizedDisplayOptions.client;
    return options.enabled && options.name;
  }

  get clientMetaLines(): string[] {
    const options = this.normalizedDisplayOptions.client;
    if (!options.enabled) return [];
    const addressLines = this.resolvedBillToAddressLines;
    return [
      ...(options.email ? [this.billToMeta] : []),
      ...(options.phone ? [this.billToPhone] : []),
      ...(options.address ? addressLines : []),
    ]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);
  }

  private get resolvedBillToAddressLines(): string[] {
    const fromLines = filterPlaceholderAddressLines(this.billToAddressLines);
    if (fromLines.length) return fromLines;
    return splitMultilineAddressLines(this.billToAddress);
  }

  get showPaymentTerms(): boolean {
    return this.normalizedDisplayOptions.paymentTerms;
  }

  get showTermsAndConditions(): boolean {
    return this.normalizedDisplayOptions.termsAndConditions;
  }

  private splitLines(value: string): string[] {
    return String(value ?? "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }
}

